import {css} from 'styled-components'
import type {SimpleInterpolation} from 'styled-components'

type Interpolation = SimpleInterpolation

const STYLE_ELEMENT_ID = 'purview-view-transition-styles'

let transitionCounter = 0

const ensureStyleElement = () => {
	if (typeof document === 'undefined') {
		return null
	}
	let styleEl = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null
	if (!styleEl) {
		styleEl = document.createElement('style')
		styleEl.id = STYLE_ELEMENT_ID
		document.head.appendChild(styleEl)
	}
	return styleEl
}

const injectedKeyframes = new Set<string>()

const isKeyframes = (value: unknown): value is {name: string; rules: string} => {
	return Boolean(
		value
		&& typeof value === 'object'
		&& 'name' in value
		&& 'rules' in value
		&& typeof (value as {name: unknown}).name === 'string'
		&& typeof (value as {rules: unknown}).rules === 'string'
	)
}

const ensureKeyframesInjected = (keyframes: {name: string; rules: string}) => {
	if (injectedKeyframes.has(keyframes.name)) {
		return
	}
	const rule = keyframes.rules.includes('@keyframes')
		? keyframes.rules
		: `@keyframes ${keyframes.name} {${keyframes.rules}}`
	injectCssRule(rule)
	injectedKeyframes.add(keyframes.name)
}

const flattenCss = (value: unknown): string => {
	if (Array.isArray(value)) {
		return value.map(flattenCss).join('')
	}
	if (value === null || value === undefined || value === false) {
		return ''
	}
	if (isKeyframes(value)) {
		ensureKeyframesInjected(value)
		return value.name
	}
	return String(value)
}

const buildCss = (strings: TemplateStringsArray, interpolations: Interpolation[]) => {
	const compiled = css(strings, ...interpolations)
	return flattenCss(compiled).trim()
}

const injectCssRule = (rule: string) => {
	const styleEl = ensureStyleElement()
	if (!styleEl) {
		return
	}
	styleEl.appendChild(document.createTextNode(`${rule}\n`))
}

const createViewTransitionTag = (type: 'old' | 'new') => {
	return (strings: TemplateStringsArray, ...interpolations: Interpolation[]) => {
		let injected = false
		let className = ''
		return () => {
			if (!injected) {
				className = `vt-${Date.now().toString(36)}-${(transitionCounter += 1).toString(36)}`
				const body = buildCss(strings, interpolations)
				const rule = `::view-transition-${type}(.${className}) {\n${body}\n}`
				injectCssRule(rule)
				injected = true
			}
			return className
		}
	}
}

const createViewTransitionPairInternal = () => {
	return (newStrings: TemplateStringsArray, ...newInterpolations: Interpolation[]) => {
		return (oldStrings: TemplateStringsArray, ...oldInterpolations: Interpolation[]) => {
			let injected = false
			let className = ''
			return () => {
				if (!injected) {
					className = `vt-${Date.now().toString(36)}-${(transitionCounter += 1).toString(36)}`
					const newBody = buildCss(newStrings, newInterpolations)
					const oldBody = buildCss(oldStrings, oldInterpolations)
					const newRule = `::view-transition-new(.${className}) {\n${newBody}\n}`
					const oldRule = `::view-transition-old(.${className}) {\n${oldBody}\n}`
					injectCssRule(newRule)
					injectCssRule(oldRule)
					injected = true
				}
				return className
			}
		}
	}
}

export const viewTransitionOld = createViewTransitionTag('old')
export const viewTransitionNew = createViewTransitionTag('new')
export const createViewTransitionPair = createViewTransitionPairInternal()
